import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  ShieldMinus,
  ShieldPlus,
  UserCheck,
  UserX,
} from 'lucide-react'
import { ConfirmDialog } from '../question-bank/ConfirmDialog'
import {
  AdminEmptyState,
  AdminErrorBanner,
  AdminPagination,
  AdminSearchBar,
} from './AdminTableChrome'
import {
  ADMIN_PAGE_SIZE,
  adminKeys,
  formatDateAr,
  type AdminUser,
  type AdminUserListResponse,
} from './types'

type UserAction = 'disable' | 'enable' | 'grantAdmin' | 'revokeAdmin'

const ADMIN_ROLE = 'Admin'

/**
 * Maps a backend guard problem `code` to an `admin`-namespace i18n key. The
 * user-mutation endpoints return a 400 ProblemDetails with a stable `code`
 * extension (`CannotModifySelf` / `LastAdminBlocked`); anything else falls
 * back to a generic action-failed message.
 */
function guardMessageKey(code: string | undefined): 'cannotModifySelf' | 'lastAdminBlocked' | 'actionError' {
  if (code === 'CannotModifySelf') return 'cannotModifySelf'
  if (code === 'LastAdminBlocked') return 'lastAdminBlocked'
  return 'actionError'
}

/**
 * Paginated, searchable admin user table. Columns: user (name over email),
 * org, roles, status, created, actions. Row actions (disable/enable,
 * grant/revoke admin) are confirm-guarded and surface server guard errors
 * (`cannotModifySelf`, `lastAdminBlocked`) inline near the action. Renders a
 * semantic `<table>` from `md` up and collapses to stacked label:value cards
 * below it.
 */
export function AdminUserTable() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [confirm, setConfirm] = useState<{ user: AdminUser; action: UserAction } | null>(null)
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null)

  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const usersQuery = useQuery({
    queryKey: adminKeys.users({ search, page }),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      params.set('page', String(page))
      params.set('pageSize', String(ADMIN_PAGE_SIZE))
      const res = await fetch(`/api/v1/admin/users?${params.toString()}`, {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load users: ${res.status}`)
      }
      return (await res.json()) as AdminUserListResponse
    },
  })

  const actionMutation = useMutation({
    mutationFn: async ({ user, action }: { user: AdminUser; action: UserAction }) => {
      const base = `/api/v1/admin/users/${encodeURIComponent(user.id)}`
      const target =
        action === 'disable'
          ? { url: `${base}/disable`, method: 'POST' }
          : action === 'enable'
            ? { url: `${base}/enable`, method: 'POST' }
            : action === 'grantAdmin'
              ? { url: `${base}/roles/admin`, method: 'POST' }
              : { url: `${base}/roles/admin`, method: 'DELETE' }

      const res = await fetch(target.url, { method: target.method, credentials: 'include' })
      if (!res.ok) {
        let code: string | undefined
        try {
          const body = (await res.json()) as { code?: string }
          code = body.code
        } catch {
          code = undefined
        }
        const message = t(guardMessageKey(code))
        const err = new Error(message) as Error & { userId: string }
        err.userId = user.id
        throw err
      }
    },
    onMutate: () => {
      setRowError(null)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.usersRoot })
    },
    onError: (error: Error & { userId?: string }) => {
      if (error.userId) {
        setRowError({ id: error.userId, message: error.message })
      }
    },
  })

  function isPending(userId: string, action: UserAction): boolean {
    return (
      actionMutation.isPending &&
      actionMutation.variables?.user.id === userId &&
      actionMutation.variables.action === action
    )
  }

  function rowBusy(userId: string): boolean {
    return actionMutation.isPending && actionMutation.variables?.user.id === userId
  }

  const total = usersQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / ADMIN_PAGE_SIZE))
  const users = usersQuery.data?.users ?? []

  const confirmCopy = confirm ? confirmCopyFor(confirm.action) : null

  return (
    <div>
      <div className="mb-6">
        <AdminSearchBar
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('searchUsers')}
        />
      </div>

      {usersQuery.isLoading ? <TableSkeleton /> : null}

      {usersQuery.isError ? <AdminErrorBanner message={t('loadError')} /> : null}

      {usersQuery.isSuccess && users.length === 0 ? (
        <AdminEmptyState message={search ? t('noResults') : t('noUsers')} />
      ) : null}

      {usersQuery.isSuccess && users.length > 0 ? (
        <>
          {/* Desktop: semantic table. */}
          <div className="hidden overflow-hidden rounded-2xl border border-brand-100 bg-white md:block">
            <table className="w-full border-collapse text-sm" dir="rtl">
              <thead>
                <tr className="border-b border-brand-100 bg-brand-50/40 text-start text-xs font-semibold uppercase tracking-wide text-brand-600">
                  <th scope="col" className="px-4 py-3 text-start">{t('userColUser')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('userColOrg')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('userColRoles')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('userColStatus')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('userColCreated')}</th>
                  <th scope="col" className="px-4 py-3 text-end">{t('userColActions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-brand-50 last:border-0 hover:bg-brand-50/30">
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-brand-900">{user.displayName}</div>
                      <div className="text-xs text-brand-600" dir="ltr">{user.email ?? '—'}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700">
                      {user.organizationName || t('noOrg')}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <RolesPills roles={user.roles} noRolesLabel={t('noRoles')} />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StatusBadge
                        lockedOut={user.isLockedOut}
                        activeLabel={t('statusActive')}
                        disabledLabel={t('statusDisabled')}
                      />
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700">
                      <span dir="ltr">{formatDateAr(user.createdAt)}</span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <UserActions
                        user={user}
                        busy={rowBusy(user.id)}
                        isPending={isPending}
                        onAction={(action) => setConfirm({ user, action })}
                        labels={{
                          disable: t('actionDisable'),
                          enable: t('actionEnable'),
                          grantAdmin: t('actionGrantAdmin'),
                          revokeAdmin: t('actionRevokeAdmin'),
                        }}
                      />
                      {rowError?.id === user.id ? (
                        <p className="mt-1.5 text-xs text-red-600" role="alert">
                          {rowError.message}
                        </p>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked label:value cards. */}
          <ul role="list" className="space-y-4 md:hidden">
            {users.map((user) => (
              <li
                key={user.id}
                className="rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-4 pe-4 py-4 shadow-sm"
              >
                <div className="font-semibold text-brand-900">{user.displayName}</div>
                <div className="text-xs text-brand-600" dir="ltr">{user.email ?? '—'}</div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-brand-500">{t('userColOrg')}</dt>
                  <dd className="text-brand-800 text-end">{user.organizationName || t('noOrg')}</dd>
                  <dt className="text-brand-500">{t('userColStatus')}</dt>
                  <dd className="text-end">
                    <StatusBadge
                      lockedOut={user.isLockedOut}
                      activeLabel={t('statusActive')}
                      disabledLabel={t('statusDisabled')}
                    />
                  </dd>
                  <dt className="text-brand-500">{t('userColCreated')}</dt>
                  <dd className="text-brand-800 text-end" dir="ltr">{formatDateAr(user.createdAt)}</dd>
                </dl>
                <div className="mt-3">
                  <RolesPills roles={user.roles} noRolesLabel={t('noRoles')} />
                </div>
                <div className="mt-3">
                  <UserActions
                    user={user}
                    busy={rowBusy(user.id)}
                    isPending={isPending}
                    onAction={(action) => setConfirm({ user, action })}
                    labels={{
                      disable: t('actionDisable'),
                      enable: t('actionEnable'),
                      grantAdmin: t('actionGrantAdmin'),
                      revokeAdmin: t('actionRevokeAdmin'),
                    }}
                  />
                  {rowError?.id === user.id ? (
                    <p className="mt-1.5 text-xs text-red-600" role="alert">
                      {rowError.message}
                    </p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>

          <AdminPagination
            page={page}
            pageCount={pageCount}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
          />
        </>
      ) : null}

      {usersQuery.isLoading ? (
        <span className="sr-only" role="status" aria-live="polite">
          {tCommon('loading')}
        </span>
      ) : null}

      {confirm && confirmCopy ? (
        <ConfirmDialog
          open
          title={t(confirmCopy.titleKey)}
          message={t(confirmCopy.messageKey)}
          confirmLabel={t(confirmCopy.confirmKey)}
          confirmPending={actionMutation.isPending}
          onConfirm={() => {
            actionMutation.mutate(confirm)
            setConfirm(null)
          }}
          onClose={() => setConfirm(null)}
        />
      ) : null}
    </div>
  )
}

type ConfirmCopy = {
  titleKey: 'confirmDisableTitle' | 'confirmEnableTitle' | 'confirmGrantAdminTitle' | 'confirmRevokeAdminTitle'
  messageKey: 'confirmDisable' | 'confirmEnable' | 'confirmGrantAdmin' | 'confirmRevokeAdmin'
  confirmKey: 'actionDisable' | 'actionEnable' | 'actionGrantAdmin' | 'actionRevokeAdmin'
}

function confirmCopyFor(action: UserAction): ConfirmCopy {
  switch (action) {
    case 'disable':
      return { titleKey: 'confirmDisableTitle', messageKey: 'confirmDisable', confirmKey: 'actionDisable' }
    case 'enable':
      return { titleKey: 'confirmEnableTitle', messageKey: 'confirmEnable', confirmKey: 'actionEnable' }
    case 'grantAdmin':
      return { titleKey: 'confirmGrantAdminTitle', messageKey: 'confirmGrantAdmin', confirmKey: 'actionGrantAdmin' }
    case 'revokeAdmin':
      return { titleKey: 'confirmRevokeAdminTitle', messageKey: 'confirmRevokeAdmin', confirmKey: 'actionRevokeAdmin' }
  }
}

type UserActionsProps = {
  user: AdminUser
  busy: boolean
  isPending: (userId: string, action: UserAction) => boolean
  onAction: (action: UserAction) => void
  labels: Record<UserAction, string>
}

function UserActions({ user, busy, isPending, onAction, labels }: UserActionsProps) {
  const isUserAdmin = user.roles.includes(ADMIN_ROLE)
  const statusAction: UserAction = user.isLockedOut ? 'enable' : 'disable'
  const roleAction: UserAction = isUserAdmin ? 'revokeAdmin' : 'grantAdmin'

  return (
    <div className="flex items-center justify-end gap-1.5">
      <IconAction
        label={labels[statusAction]}
        pending={isPending(user.id, statusAction)}
        disabled={busy}
        destructive={statusAction === 'disable'}
        onClick={() => onAction(statusAction)}
        icon={statusAction === 'disable' ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
      />
      <IconAction
        label={labels[roleAction]}
        pending={isPending(user.id, roleAction)}
        disabled={busy}
        destructive={roleAction === 'revokeAdmin'}
        onClick={() => onAction(roleAction)}
        icon={roleAction === 'grantAdmin' ? <ShieldPlus className="h-4 w-4" /> : <ShieldMinus className="h-4 w-4" />}
      />
    </div>
  )
}

type IconActionProps = {
  label: string
  pending: boolean
  disabled: boolean
  destructive: boolean
  onClick: () => void
  icon: React.ReactNode
}

function IconAction({ label, pending, disabled, destructive, onClick, icon }: IconActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={
        'inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-40 disabled:cursor-not-allowed ' +
        (destructive
          ? 'border-red-200 text-red-600 hover:bg-red-50'
          : 'border-brand-200 text-brand-700 hover:bg-brand-50')
      }
    >
      {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <span aria-hidden="true">{icon}</span>}
    </button>
  )
}

function RolesPills({ roles, noRolesLabel }: { roles: string[]; noRolesLabel: string }) {
  if (roles.length === 0) {
    return <span className="text-xs text-brand-400">{noRolesLabel}</span>
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {roles.map((role) => (
        <span
          key={role}
          className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-semibold text-brand-700"
        >
          {role}
        </span>
      ))}
    </div>
  )
}

function StatusBadge({
  lockedOut,
  activeLabel,
  disabledLabel,
}: {
  lockedOut: boolean
  activeLabel: string
  disabledLabel: string
}) {
  return lockedOut ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden="true" />
      {disabledLabel}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
      {activeLabel}
    </span>
  )
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-brand-100 bg-white" aria-hidden="true">
      <div className="space-y-px">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-4 animate-pulse">
            <div className="h-9 flex-1 rounded bg-brand-50" />
            <div className="h-9 w-24 rounded bg-brand-50" />
            <div className="h-9 w-20 rounded bg-brand-50" />
          </div>
        ))}
      </div>
    </div>
  )
}
